/**
 * A inscrição no push do celular — o lado da tela.
 *
 * O fluxo inteiro num botão: pedir permissão de notificação, inscrever o
 * navegador (chave pública VAPID do servidor) e amarrar a inscrição à pessoa
 * logada.
 *
 * POR QUE ISTO CONTA O QUE DEU ERRADO (14/08): a primeira versão devolvia só
 * "falhou", e o Lucas não conseguiu ativar nem no próprio celular — sem saber
 * o motivo, não havia o que consertar. Agora cada passo diz o que aconteceu,
 * e `diagnosticoDoAparelho()` mostra o estado inteiro numa olhada.
 *
 * O CASO MAIS COMUM, e o mais silencioso: no iPhone, a Apple só permite push
 * em app ADICIONADO À TELA DE INÍCIO (iOS 16.4+). No Safari normal o botão
 * existe, a API existe, e a inscrição falha — parece defeito do sistema, mas
 * é regra do aparelho.
 */

import { api } from '../api.js';

export function suportaPush(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** Rodando como app instalado (tela de início / atalho), não numa aba. */
export function comoAppInstalado(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function ehIphone(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export async function registrarServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    await navigator.serviceWorker.register('/sw.js');
  } catch {
    // Sem service worker, sem push — o app segue normal.
  }
}

/** A pessoa já está inscrita NESTE navegador? */
export async function jaInscrito(): Promise<boolean> {
  if (!suportaPush()) return false;
  try {
    const sw = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((r) => setTimeout(() => r(null), 4000)),
    ]);
    if (!sw) return false;
    return (await sw.pushManager.getSubscription()) !== null;
  } catch {
    return false;
  }
}

function base64ParaBytes(base64: string): Uint8Array<ArrayBuffer> {
  const preenchida = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const crua = atob(preenchida.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(new ArrayBuffer(crua.length));
  for (let i = 0; i < crua.length; i++) bytes[i] = crua.charCodeAt(i);
  return bytes;
}

export interface DiagnosticoPush {
  suporta: boolean;
  permissao: NotificationPermission | 'sem-suporte';
  appInstalado: boolean;
  iphone: boolean;
  serviceWorker: 'ativo' | 'registrando' | 'ausente';
  inscrito: boolean;
  /** O que impede AGORA, em português, quando algo impede. */
  impedimento: string | null;
}

/**
 * O estado do push NESTE aparelho, item por item. É o que a central de avisos
 * mostra e o que transforma "não funciona" em "falta tal coisa".
 */
export async function diagnosticoDoAparelho(): Promise<DiagnosticoPush> {
  const iphone = ehIphone();
  const appInstalado = comoAppInstalado();
  const suporta = suportaPush();
  if (!suporta) {
    return {
      suporta: false,
      permissao: 'sem-suporte',
      appInstalado,
      iphone,
      serviceWorker: 'ausente',
      inscrito: false,
      impedimento: iphone
        ? 'Este iPhone precisa de iOS 16.4 ou mais novo, e o app precisa estar na Tela de Início.'
        : 'Este navegador não tem suporte a notificação. Use o Chrome (Android/computador) ou o Safari do iPhone com o app instalado.',
    };
  }

  let serviceWorker: DiagnosticoPush['serviceWorker'] = 'ausente';
  let inscrito = false;
  try {
    const sw = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((r) => setTimeout(() => r(null), 4000)),
    ]);
    if (sw) {
      serviceWorker = 'ativo';
      inscrito = (await sw.pushManager.getSubscription()) !== null;
    } else {
      serviceWorker = (await navigator.serviceWorker.getRegistration()) ? 'registrando' : 'ausente';
    }
  } catch {
    serviceWorker = 'ausente';
  }

  const permissao = Notification.permission;
  let impedimento: string | null = null;
  if (iphone && !appInstalado) {
    impedimento =
      'No iPhone o aviso só funciona com o app na Tela de Início: toque em compartilhar (□↑) → "Adicionar à Tela de Início", abra pelo ícone e ative por lá.';
  } else if (permissao === 'denied') {
    impedimento =
      'A notificação está BLOQUEADA para este site. Libere nos ajustes do navegador (cadeado ao lado do endereço → Notificações → Permitir) e tente de novo.';
  } else if (serviceWorker === 'ausente') {
    impedimento = 'O app ainda não terminou de se instalar neste navegador — recarregue a página e tente de novo.';
  }

  return { suporta, permissao, appInstalado, iphone, serviceWorker, inscrito, impedimento };
}

export type ResultadoAtivacao =
  | { estado: 'ativado' }
  | { estado: 'negado'; detalhe: string }
  | { estado: 'sem-suporte'; detalhe: string }
  | { estado: 'falhou'; detalhe: string };

/**
 * Pede a permissão e inscreve, contando em que passo parou. O `detalhe` é
 * escrito para quem vai LER na tela, com o erro técnico entre parênteses —
 * quem administra precisa dos dois.
 */
export async function ativarPush(): Promise<ResultadoAtivacao> {
  const d = await diagnosticoDoAparelho();
  if (!d.suporta) return { estado: 'sem-suporte', detalhe: d.impedimento ?? 'Sem suporte a push.' };
  if (d.iphone && !d.appInstalado) {
    return { estado: 'sem-suporte', detalhe: d.impedimento! };
  }

  let permissao: NotificationPermission;
  try {
    permissao = await Notification.requestPermission();
  } catch (erro) {
    return { estado: 'falhou', detalhe: `O navegador recusou o pedido de permissão (${nomeDoErro(erro)}).` };
  }
  if (permissao !== 'granted') {
    return {
      estado: 'negado',
      detalhe:
        permissao === 'denied'
          ? 'A permissão está bloqueada para este site. Libere no cadeado ao lado do endereço → Notificações → Permitir.'
          : 'A permissão não foi concedida — toque de novo e escolha "Permitir".',
    };
  }

  try {
    const { chave } = await api.get<{ chave: string }>('/api/push/chave-publica');

    const sw = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((r) => setTimeout(() => r(null), 10_000)),
    ]);
    if (!sw) {
      return {
        estado: 'falhou',
        detalhe: 'O app não terminou de se instalar neste navegador (service worker). Recarregue a página e tente de novo.',
      };
    }

    const inscricao =
      (await sw.pushManager.getSubscription()) ??
      (await sw.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64ParaBytes(chave),
      }));

    const dados = inscricao.toJSON();
    if (!dados.endpoint || !dados.keys?.['p256dh'] || !dados.keys?.['auth']) {
      return { estado: 'falhou', detalhe: 'O navegador devolveu uma inscrição incompleta.' };
    }
    await api.post('/api/push/inscricao', {
      endpoint: dados.endpoint,
      chaves: { p256dh: dados.keys['p256dh'], auth: dados.keys['auth'] },
    });
    return { estado: 'ativado' };
  } catch (erro) {
    return { estado: 'falhou', detalhe: explicar(erro) };
  }
}

function nomeDoErro(erro: unknown): string {
  if (erro instanceof Error) return `${erro.name}: ${erro.message}`.slice(0, 160);
  return String(erro).slice(0, 160);
}

/** Traduz os erros que o `subscribe` costuma dar para linguagem de gente. */
function explicar(erro: unknown): string {
  const bruto = nomeDoErro(erro);
  if (/not supported|NotSupportedError/i.test(bruto)) {
    return ehIphone()
      ? 'Este iPhone não permite avisos aqui — abra o app pelo ícone da Tela de Início e tente por lá. ' + `(${bruto})`
      : 'Este navegador não suporta o serviço de push. ' + `(${bruto})`;
  }
  if (/NotAllowedError|permission/i.test(bruto)) {
    return 'O navegador bloqueou a inscrição — libere as notificações para este site e tente de novo. ' + `(${bruto})`;
  }
  if (/AbortError|registration failed|push service/i.test(bruto)) {
    return 'O serviço de push do navegador recusou a inscrição (costuma ser rede ou o Google Play Services no Android). ' + `(${bruto})`;
  }
  return 'Não consegui ativar. ' + `(${bruto})`;
}
